import { useEffect, useState } from "react";
import { X, Loader2, Search, ChevronDown, RefreshCw, ArrowRight, Check } from "lucide-react";
import { format } from "date-fns";
import {
  BASE_CURRENCY,
  convertToMyr,
  describeCurrency,
  fetchMyrExchangeRates,
  type CurrencyOption,
  type ExchangeRateSnapshot,
} from "../../api/exchangeRateApi";

interface CurrencyConverterModalProps {
  onClose: () => void;
}

const DEFAULT_CURRENCY = "USD";

const myrFormatter = new Intl.NumberFormat("en-MY", {
  style: "currency",
  currency: BASE_CURRENCY,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const rateFormatter = new Intl.NumberFormat("en-MY", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
});

export function CurrencyConverterModal({ onClose }: CurrencyConverterModalProps) {
  const [snapshot, setSnapshot] = useState<ExchangeRateSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [selectedCode, setSelectedCode] = useState(DEFAULT_CURRENCY);
  const [amountText, setAmountText] = useState("1");
  const [isListOpen, setIsListOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetchMyrExchangeRates(controller.signal)
      .then((data) => {
        setSnapshot(data);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("Error fetching exchange rates", err);
        setError("Could not load live exchange rates. Check your internet connection and try again.");
        setLoading(false);
      });

    return () => controller.abort();
  }, [reloadToken]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (isListOpen) setIsListOpen(false);
        else onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isListOpen, onClose]);

  const currencies: CurrencyOption[] = snapshot
    ? Object.keys(snapshot.rates)
        .filter((code) => code !== BASE_CURRENCY)
        .map(describeCurrency)
        .sort((a, b) => a.code.localeCompare(b.code))
    : [];

  const normalizedSearch = search.trim().toLowerCase();
  const visibleCurrencies = normalizedSearch
    ? currencies.filter(
        (c) =>
          c.code.toLowerCase().includes(normalizedSearch) ||
          c.name.toLowerCase().includes(normalizedSearch)
      )
    : currencies;

  const selected = describeCurrency(selectedCode);
  const amount = Number(amountText.replace(/,/g, ""));
  const hasValidAmount = amountText.trim() !== "" && Number.isFinite(amount) && amount >= 0;
  const convertedMyr =
    snapshot && hasValidAmount ? convertToMyr(amount, selectedCode, snapshot.rates) : null;
  const unitRateMyr = snapshot ? convertToMyr(1, selectedCode, snapshot.rates) : null;

  const handleSelect = (code: string) => {
    setSelectedCode(code);
    setIsListOpen(false);
    setSearch("");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="flex w-full max-w-xl flex-col max-h-[90vh] rounded-xl bg-white shadow-2xl overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="currency-converter-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4 bg-slate-50">
          <div>
            <h2 id="currency-converter-title" className="text-lg font-bold text-slate-800">
              Currency Converter
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Convert any currency to Malaysian Ringgit (MYR) using live rates
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-200 hover:text-slate-700 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 bg-slate-50/50">
          {loading ? (
            <div className="flex h-48 flex-col items-center justify-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <p className="text-sm font-medium text-slate-500">Loading live exchange rates...</p>
            </div>
          ) : error ? (
            <div className="flex h-48 flex-col items-center justify-center gap-4 text-center">
              <p className="text-red-500 font-medium">{error}</p>
              <button
                type="button"
                onClick={() => setReloadToken((n) => n + 1)}
                className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Retry
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Currency picker */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">From Currency</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsListOpen((open) => !open)}
                    className="flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2.5 text-left text-sm hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors"
                    aria-haspopup="listbox"
                    aria-expanded={isListOpen}
                  >
                    <span className="flex items-center gap-3">
                      <span className="text-xl leading-none">{selected.flag}</span>
                      <span className="font-bold text-slate-800">{selected.code}</span>
                      <span className="text-slate-500">{selected.name}</span>
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-slate-400 transition-transform ${
                        isListOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {isListOpen && (
                    <div className="absolute left-0 right-0 z-10 mt-1 rounded-lg border border-slate-200 bg-white shadow-xl overflow-hidden">
                      <div className="flex items-center gap-2 border-b px-3 py-2 bg-slate-50">
                        <Search className="h-4 w-4 text-slate-400" />
                        <input
                          autoFocus
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search country or currency (e.g. USD, Euro, Singapore)"
                          className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
                        />
                        <span className="text-[11px] font-medium text-slate-400 whitespace-nowrap">
                          {visibleCurrencies.length} of {currencies.length}
                        </span>
                      </div>
                      <ul role="listbox" className="max-h-64 overflow-auto py-1">
                        {visibleCurrencies.length === 0 ? (
                          <li className="px-4 py-6 text-center text-sm text-slate-500">
                            No currency matches "{search}"
                          </li>
                        ) : (
                          visibleCurrencies.map((c) => {
                            const isSelected = c.code === selectedCode;
                            return (
                              <li key={c.code} role="option" aria-selected={isSelected}>
                                <button
                                  type="button"
                                  onClick={() => handleSelect(c.code)}
                                  className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors ${
                                    isSelected
                                      ? "bg-blue-50 text-blue-700"
                                      : "hover:bg-slate-100 text-slate-700"
                                  }`}
                                >
                                  <span className="text-lg leading-none">{c.flag}</span>
                                  <span className="w-12 font-bold">{c.code}</span>
                                  <span className="flex-1 truncate">{c.name}</span>
                                  {isSelected && <Check className="h-4 w-4 text-blue-600" />}
                                </button>
                              </li>
                            );
                          })
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Amount */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="currency-converter-amount" className="text-xs font-semibold text-slate-600">
                  Amount in {selected.code}
                </label>
                <div className="flex items-center rounded-md border border-slate-300 bg-white focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-colors">
                  <span className="px-3 text-sm font-bold text-slate-500 border-r border-slate-200 py-2.5">
                    {selected.code}
                  </span>
                  <input
                    id="currency-converter-amount"
                    type="text"
                    inputMode="decimal"
                    value={amountText}
                    onChange={(e) => setAmountText(e.target.value)}
                    onFocus={(e) => e.target.select()}
                    placeholder="0.00"
                    className="flex-1 bg-transparent px-3 py-2.5 text-lg font-semibold text-slate-800 outline-none"
                  />
                </div>
                {!hasValidAmount && amountText.trim() !== "" && (
                  <p className="text-xs text-red-500">Enter a valid positive number.</p>
                )}
              </div>

              {/* Result */}
              <div className="rounded-lg border border-blue-200 bg-blue-50 px-5 py-4">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-blue-600">
                  <span>{selected.code}</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                  <span>{BASE_CURRENCY} (Malaysian Ringgit)</span>
                </div>
                <p className="mt-1 text-3xl font-bold text-slate-900">
                  {convertedMyr === null ? "—" : myrFormatter.format(convertedMyr)}
                </p>
                <p className="mt-2 text-xs text-slate-600">
                  {unitRateMyr === null
                    ? `No rate available for ${selected.code}.`
                    : `1 ${selected.code} = ${rateFormatter.format(unitRateMyr)} ${BASE_CURRENCY}`}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t px-6 py-3 bg-white">
          <div className="text-[11px] text-slate-500">
            {snapshot?.lastUpdated ? (
              <>
                Rates updated {format(snapshot.lastUpdated, "dd MMM yyyy, HH:mm")}
                {" · "}
                <button
                  type="button"
                  onClick={() => setReloadToken((n) => n + 1)}
                  className="font-semibold text-blue-600 hover:underline"
                >
                  Refresh
                </button>
              </>
            ) : (
              "Source: ExchangeRate-API (free tier)"
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-slate-800 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-700 transition-colors active:scale-95"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
